import type { Booking } from "../types/booking";
import type { InvoiceLine } from "../types/invoice";
import { generateInvoiceLinesFromPricing } from "./pricingCalculationService";

const isInklaringLine = (l: InvoiceLine): boolean =>
  /inklaringskosten/i.test(l.description || "") ||
  /customs clearance/i.test(l.description || "") ||
  l.calculation_type === "per_unique_product" ||
  l.calculation_type === "per_product";

/**
 * Recompute Inklaringskosten / customs-clearance lines from the booking's
 * current unique HS-code count and replace matching lines on the invoice.
 * No-op when the invoice has no inklaring line yet.
 */
export async function syncInklaringskostenInvoiceLines(
  booking: Booking,
  lines: InvoiceLine[]
): Promise<{ lines: InvoiceLine[]; changed: boolean }> {
  if (!lines?.length || !lines.some(isInklaringLine)) {
    return { lines, changed: false };
  }

  const { lines: generated, error } = await generateInvoiceLinesFromPricing(booking);
  if (error || !generated?.length) return { lines, changed: false };

  const newInklaring = generated.filter(isInklaringLine);
  if (newInklaring.length === 0) return { lines, changed: false };

  const oldInklaring = lines.filter(isInklaringLine);
  const kept = lines.filter((l) => !isInklaringLine(l));
  const merged = newInklaring.map((g, i) => {
    const prev = oldInklaring[i] ?? oldInklaring[0];
    return {
      ...g,
      id: prev?.id ?? g.id,
      vat_percentage: prev?.vat_percentage ?? g.vat_percentage ?? 0,
    };
  });

  const same =
    oldInklaring.length === merged.length &&
    oldInklaring.every((old, i) => {
      const next = merged[i];
      return (
        old.quantity === next.quantity &&
        Math.abs((old.unit_price || 0) - (next.unit_price || 0)) < 0.01 &&
        (old.description || "") === (next.description || "")
      );
    });

  if (same) return { lines, changed: false };
  return { lines: [...kept, ...merged], changed: true };
}
